import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X } from 'lucide-react';
import api from '../lib/api';
import ProductCard from '../components/ProductCard';
import { CATEGORIES, setPageMeta } from '../lib/shop';

const SORTS = [
  ['featured', 'Featured'], ['newest', 'Newest'], ['price_asc', 'Price: Low to High'],
  ['price_desc', 'Price: High to Low'], ['az', 'A–Z'],
];

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ categories: [], formats: [], strengths: [] });
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [f, setF] = useState({
    q: params.get('q') || '', category: params.get('category') || '',
    format: params.get('format') || '', strength: '', availability: '',
    coa: '', popular: '', new: '', sort: params.get('sort') || 'featured',
    min_price: '', max_price: '',
  });

  useEffect(() => { setPageMeta('Research Catalogue', 'Browse the full VQ Health research compound catalogue.'); }, []);
  useEffect(() => { api.get('/products/meta').then((r) => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const qp = {};
    Object.entries(f).forEach(([k, v]) => { if (v) qp[k] = v; });
    api.get('/products', { params: qp }).then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [f]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: p[k] === v ? '' : v }));
  const setVal = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const activeCount = useMemo(() =>
    ['category', 'format', 'strength', 'availability', 'coa', 'popular', 'new', 'min_price', 'max_price'].filter((k) => f[k]).length, [f]);

  const Filters = (
    <div className="space-y-6">
      <div>
        <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95] mb-2.5">Category</p>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(CATEGORIES).map(([key, c]) => (
            <button key={key} data-testid={`filter-category-${key}`} onClick={() => set('category', key)}
              className={`px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-wider border transition-colors ${f.category === key ? 'text-white border-transparent' : 'border-[#D8D8D0] hover:border-[#0B0F12]'}`}
              style={f.category === key ? { background: c.color } : {}}>
              {c.name}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95] mb-2.5">Format</p>
        <div className="flex flex-wrap gap-1.5">
          {meta.formats.map((fm) => (
            <button key={fm} data-testid={`filter-format-${fm.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => set('format', fm)}
              className={`px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-wider border transition-colors ${f.format === fm ? 'bg-[#0B0F12] text-white border-[#0B0F12]' : 'border-[#D8D8D0] hover:border-[#0B0F12]'}`}>
              {fm}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95] mb-2.5">Strength</p>
        <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
          {meta.strengths.map((s) => (
            <button key={s} onClick={() => set('strength', s)}
              className={`px-2.5 py-1 font-mono2 text-[10px] border transition-colors ${f.strength === s ? 'bg-[#0B0F12] text-white border-[#0B0F12]' : 'border-[#D8D8D0] hover:border-[#0B0F12]'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95] mb-2.5">Price (£)</p>
        <div className="flex items-center gap-2">
          <input data-testid="filter-price-min" type="number" min="0" placeholder="Min" value={f.min_price} onChange={(e) => setVal('min_price', e.target.value)}
            className="w-full border hairline px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#0B0F12]" />
          <span className="text-[#8A8F95]">–</span>
          <input data-testid="filter-price-max" type="number" min="0" placeholder="Max" value={f.max_price} onChange={(e) => setVal('max_price', e.target.value)}
            className="w-full border hairline px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#0B0F12]" />
        </div>
      </div>
      <div className="space-y-2">
        {[['availability', 'in_stock', 'In stock only'], ['coa', '1', 'COA available'], ['popular', '1', 'Popular'], ['new', '1', 'New arrivals']].map(([k, v, label]) => (
          <label key={k} className="flex items-center gap-2.5 cursor-pointer">
            <input data-testid={`filter-${k}`} type="checkbox" checked={f[k] === v} onChange={() => set(k, v)} className="w-4 h-4 accent-[#0B0F12]" />
            <span className="text-sm">{label}</span>
          </label>
        ))}
      </div>
      {activeCount > 0 && (
        <button data-testid="filter-clear" onClick={() => setF((p) => ({ ...p, category: '', format: '', strength: '', availability: '', coa: '', popular: '', new: '', min_price: '', max_price: '' }))}
          className="font-mono2 text-[10px] uppercase tracking-widest underline underline-offset-4 text-[#575B60]">
          Clear all filters ({activeCount})
        </button>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="catalogue-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A8F95]">Research Catalogue</p>
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-2">
            {f.format ? `${f.format}s` : f.category ? CATEGORIES[f.category]?.name : 'All Products'}
          </h1>
          <p className="text-sm text-[#575B60] mt-2">{products.length} compounds {loading ? '· loading…' : 'listed'}</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="filters-toggle" onClick={() => setFiltersOpen(true)} className="lg:hidden flex items-center gap-2 border hairline px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-widest bg-white">
            <SlidersHorizontal className="w-4 h-4" /> Filters {activeCount > 0 && `(${activeCount})`}
          </button>
          <select data-testid="sort-select" value={f.sort} onChange={(e) => setVal('sort', e.target.value)}
            className="border hairline px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-widest bg-white focus:outline-none">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
        <aside className="hidden lg:block">{Filters}</aside>
        <div>
          {loading ? (
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-6">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[3/4] bg-[#F4F4F0] animate-pulse" />)}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20" data-testid="catalogue-empty">
              <p className="font-display font-bold uppercase text-2xl">No products match</p>
              <p className="text-sm text-[#575B60] mt-2">Adjust or clear the filters to see the full catalogue.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-6" data-testid="product-grid">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="filters-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setFiltersOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-[88%] max-w-sm bg-[#FAFAF8] p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <p className="font-display font-black uppercase text-xl">Filters</p>
              <button data-testid="filters-close" onClick={() => setFiltersOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            {Filters}
            <button onClick={() => setFiltersOpen(false)} className="mt-8 w-full bg-[#0B0F12] text-white py-3 font-mono2 text-xs uppercase tracking-widest">Show {products.length} results</button>
          </div>
        </div>
      )}
    </div>
  );
}
